/**
 * One cited moment: the client's preceding line, the coach quote, polarity,
 * an optional per-citation rationale, relevance toggle and a transcript jump.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { EvidenceFeedbackReason, EvidenceFeedbackVerdict } from '@/types/mentorEngagements';
import { mentorStyles } from '../shared';
import type { EvidenceMoment, RelevanceState } from './model';

export const FEEDBACK_REASONS: EvidenceFeedbackReason[] = ['wrong_statement', 'misquoted', 'not_the_coach', 'too_weak'];

interface Props {
  moment: EvidenceMoment;
  relevance: RelevanceState;
  reason?: EvidenceFeedbackReason;
  locked: boolean;
  canJump: boolean;
  pendingReason: boolean;
  onVerdict: (verdict: EvidenceFeedbackVerdict, reason?: EvidenceFeedbackReason) => void;
  onReopen: () => void;
  onJump: () => void;
}

export function EvidenceMomentCard({ moment, relevance, reason, locked, canJump, pendingReason, onVerdict, onReopen, onJump }: Props) {
  const { t } = useTranslation();
  const contradicts = moment.polarity === 'contradicts';
  const railColor = contradicts ? reviewDesk.amber : reviewDesk.accent;
  const timeLabel = moment.timestamp || (moment.turn_index != null ? t('mentorEngagements.review.turn', { number: moment.turn_index }) : t('mentorEngagements.review.evidence.noTimestamp'));
  const speakerMismatch = !contradicts && moment.speaker === 'client';

  if (relevance === 'not_relevant') {
    return (
      <Pressable testID={`sof_review_moment_${moment.key}`} accessibilityRole="button" disabled={locked} onPress={onReopen}
        style={({ pressed }) => [styles.rejected, pressed && mentorStyles.pressed]}>
        <MaterialIcons name="block" size={15} color={reviewDesk.muted} />
        <Text style={styles.rejectedText} numberOfLines={1}>
          {t('mentorEngagements.review.relevance.rejected', { reason: reason ? t(`mentorEngagements.review.relevance.reason.${reason}`) : '—' })} · {timeLabel}
        </Text>
        {!locked ? <Text style={styles.rejectedHint}>{t('mentorEngagements.review.relevance.reopen')}</Text> : null}
      </Pressable>
    );
  }

  return (
    <View testID={`sof_review_moment_${moment.key}`} style={[styles.card, { borderLeftColor: railColor }]}>
      <View style={styles.head}>
        <Text style={styles.time}>{timeLabel}{moment.timestamp && moment.turn_index != null ? ` · ${t('mentorEngagements.review.turn', { number: moment.turn_index })}` : ''}</Text>
        <View style={[styles.polarity, contradicts ? styles.polarityContra : styles.polaritySupports]}>
          <MaterialIcons name={contradicts ? 'arrow-drop-down' : 'arrow-drop-up'} size={16} color={contradicts ? '#8A5B00' : reviewDesk.accent} />
          <Text style={[styles.polarityText, { color: contradicts ? '#8A5B00' : reviewDesk.accent }]}>{t(`mentorEngagements.review.evidence.${contradicts ? 'contradicts' : 'supports'}`)}</Text>
        </View>
      </View>
      {moment.client_context ? (
        <View style={styles.line}>
          <Text style={styles.speakerMuted}>{t('mentorEngagements.review.client')}</Text>
          <Text style={styles.clientText}>“{moment.client_context}”</Text>
        </View>
      ) : null}
      <View style={styles.line}>
        <Text style={styles.speaker}>{t(`mentorEngagements.review.${moment.speaker === 'client' ? 'client' : 'coach'}`)}</Text>
        <Text style={styles.quote}>“{moment.quote}”</Text>
      </View>
      {speakerMismatch ? (
        <View style={styles.warn}>
          <MaterialIcons name="warning-amber" size={14} color="#8A5B00" />
          <Text style={styles.warnText}>{t('mentorEngagements.review.evidence.speakerMismatch')}</Text>
        </View>
      ) : null}
      {moment.rationale ? <Text style={styles.why}>{moment.rationale}</Text> : null}
      <View style={styles.actions}>
        {moment.hasKey ? (
          <View style={styles.toggle}>
            <Pressable testID={`sof_review_relevant_${moment.key}`} accessibilityRole="button" accessibilityState={{ selected: relevance === 'relevant', disabled: locked }}
              disabled={locked} onPress={() => onVerdict('relevant')}
              style={({ pressed }) => [styles.toggleButton, relevance === 'relevant' && styles.toggleOn, pressed && mentorStyles.pressed]}>
              {relevance === 'relevant' ? <MaterialIcons name="check" size={14} color="#FFFFFF" /> : null}
              <Text style={[styles.toggleText, relevance === 'relevant' && { color: '#FFFFFF' }]}>{t('mentorEngagements.review.relevance.relevant')}</Text>
            </Pressable>
            <Pressable testID={`sof_review_not_relevant_${moment.key}`} accessibilityRole="button" accessibilityState={{ selected: pendingReason, disabled: locked }}
              disabled={locked} onPress={() => onVerdict('not_relevant')}
              style={({ pressed }) => [styles.toggleButton, pendingReason && styles.togglePending, pressed && mentorStyles.pressed]}>
              <Text style={styles.toggleText}>{t('mentorEngagements.review.relevance.notRelevant')}</Text>
            </Pressable>
          </View>
        ) : (
          <Text style={styles.disabledHint}>{t('mentorEngagements.review.evidence.feedbackDisabled')}</Text>
        )}
        {canJump ? (
          <Pressable testID={`sof_review_jump_${moment.key}`} accessibilityRole="button" onPress={onJump} style={({ pressed }) => [styles.jump, pressed && mentorStyles.pressed]}>
            <Text style={styles.jumpText}>{t('mentorEngagements.review.actions.jump')}</Text>
            <MaterialIcons name="north-east" size={14} color={reviewDesk.accent} />
          </Pressable>
        ) : null}
      </View>
      {pendingReason && !locked ? (
        <View style={styles.reasons}>
          <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.relevance.reasonPrompt')}</Text>
          {FEEDBACK_REASONS.map(item => (
            <Pressable key={item} testID={`sof_review_reason_${moment.key}_${item}`} accessibilityRole="button" onPress={() => onVerdict('not_relevant', item)}
              style={({ pressed }) => [styles.reason, pressed && mentorStyles.pressed]}>
              <Text style={styles.reasonText}>{t(`mentorEngagements.review.relevance.reason.${item}`)}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1, gap: 8, padding: 14, paddingLeft: 16, borderRadius: 14,
    borderWidth: 1, borderColor: reviewDesk.line, borderLeftWidth: 4,
    backgroundColor: reviewDesk.card, minWidth: 0,
  },
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  time: { color: reviewDesk.muted, fontSize: 12, fontWeight: '700', fontVariant: ['tabular-nums'] },
  polarity: { flexDirection: 'row', alignItems: 'center', paddingRight: 8, paddingLeft: 2, borderRadius: 999 },
  polaritySupports: { backgroundColor: reviewDesk.accentSoft },
  polarityContra: { backgroundColor: reviewDesk.amberSoft },
  polarityText: { fontSize: 11, fontWeight: '800' },
  line: { gap: 2 },
  speaker: { color: reviewDesk.ink, fontSize: 11, fontWeight: '800' },
  speakerMuted: { color: reviewDesk.muted, fontSize: 11, fontWeight: '800' },
  clientText: { color: reviewDesk.muted, fontSize: 14, lineHeight: 20 },
  quote: { color: reviewDesk.ink, fontSize: 15, lineHeight: 23 },
  warn: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  warnText: { color: '#8A5B00', fontSize: 12, fontWeight: '600' },
  why: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19, fontStyle: 'italic' },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 2 },
  toggle: { flexDirection: 'row', gap: 6 },
  toggleButton: { flexDirection: 'row', alignItems: 'center', gap: 4, minHeight: 32, paddingHorizontal: 12, borderRadius: 999, borderWidth: 1, borderColor: reviewDesk.lineStrong, backgroundColor: reviewDesk.card },
  toggleOn: { backgroundColor: reviewDesk.accent, borderColor: reviewDesk.accent },
  togglePending: { borderColor: '#8A5B00', backgroundColor: reviewDesk.amberSoft },
  toggleText: { fontSize: 12, fontWeight: '700', color: reviewDesk.inkSoft },
  disabledHint: { color: reviewDesk.muted, fontSize: 12, fontStyle: 'italic', flexShrink: 1 },
  jump: { flexDirection: 'row', alignItems: 'center', gap: 2, minHeight: 32, paddingHorizontal: 6 },
  jumpText: { color: reviewDesk.accent, fontSize: 12, fontWeight: '700' },
  reasons: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  reason: { paddingVertical: 5, paddingHorizontal: 10, borderRadius: 999, borderWidth: 1, borderColor: '#F0D9A8', backgroundColor: reviewDesk.amberSoft },
  reasonText: { fontSize: 12, fontWeight: '600', color: '#8A5B00' },
  rejected: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10, backgroundColor: reviewDesk.cardMuted, minWidth: 0 },
  rejectedText: { flex: 1, color: reviewDesk.muted, fontSize: 12, fontWeight: '600' },
  rejectedHint: { color: reviewDesk.accent, fontSize: 11, fontWeight: '700' },
});
